import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Logo } from '../components/Logo';

export default function PrivacyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center gap-3 px-4 py-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-full"
          >
            <ArrowLeft size={20} />
          </Button>
          <h1 className="font-heading font-bold text-lg">Privacy Policy</h1>
        </div>
      </header>

      <main className="px-4 py-6 max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <Logo className="h-10" />
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#8BB89F]/10 text-[#8BB89F]">
              <Shield size={14} />
              <span className="text-xs font-medium">HIPAA-conscious / secure access</span>
            </div>
          </div>
          
          <Card className="glass-card p-6 space-y-6">
            <section>
              <h2 className="font-heading font-bold text-lg mb-3">Our Commitment to Your Privacy</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                At HealthLink360, we take your privacy seriously. We maintain HIPAA-adjacent standards 
                to ensure your health information is handled with the utmost care and security. This 
                policy explains how we collect, use, and protect your personal and health data.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-bold text-lg mb-3">1. Information We Collect</h2>
              <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                We collect the following types of information:
              </p>
              <ul className="text-sm text-muted-foreground space-y-2 ml-4 list-disc">
                <li><strong>Account Information:</strong> First name, last name, email address, date of birth</li>
                <li><strong>Health Profile:</strong> Height, weight, health goals, chronic conditions, dietary preferences, activity level</li>
                <li><strong>Biometric Data:</strong> Heart rate, HRV, sleep patterns, stress levels, SpO2, steps (from wearables or facial scanning)</li>
                <li><strong>Mood & Wellness:</strong> Daily mood check-ins, habit completion, symptom reports</li>
                <li><strong>Device Data:</strong> Information from connected wearables and health devices</li>
                <li><strong>Usage Data:</strong> How you interact with our app, features used, session duration</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading font-bold text-lg mb-3">2. How We Use Your Information</h2>
              <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                Your information is used to:
              </p>
              <ul className="text-sm text-muted-foreground space-y-2 ml-4 list-disc">
                <li>Calculate your personalized Digital Twin Score</li>
                <li>Provide AI-powered wellness recommendations through Coach360</li>
                <li>Generate personalized meal and workout plans</li>
                <li>Track your habits and wellness trends over time</li>
                <li>Connect you with relevant community resources (optional)</li>
                <li>Improve our services and develop new features</li>
                <li>Send you important account and service notifications</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading font-bold text-lg mb-3">3. Data Security</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                We implement HIPAA-adjacent standards to protect your data, including:
              </p>
              <ul className="text-sm text-muted-foreground space-y-2 ml-4 list-disc mt-3">
                <li>End-to-end encryption for data transmission</li>
                <li>Secure, encrypted storage of all personal and health data</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>Strict access controls limiting who can view your information</li>
                <li>Automatic session timeouts and secure authentication</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading font-bold text-lg mb-3">4. Data Sharing</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                We do NOT sell your personal data. We may share information only in these circumstances:
              </p>
              <ul className="text-sm text-muted-foreground space-y-2 ml-4 list-disc mt-3">
                <li><strong>With your consent:</strong> When you explicitly choose to share data with healthcare providers</li>
                <li><strong>Service providers:</strong> Trusted third parties who help operate our service (under strict confidentiality)</li>
                <li><strong>Legal requirements:</strong> When required by law or to protect rights and safety</li>
                <li><strong>Aggregated data:</strong> Anonymous, aggregated insights for research (never individually identifiable)</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading font-bold text-lg mb-3">5. Your Rights</h2>
              <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                You have the right to:
              </p>
              <ul className="text-sm text-muted-foreground space-y-2 ml-4 list-disc">
                <li>Access and download your personal data</li>
                <li>Correct inaccurate information</li>
                <li>Delete your account and associated data</li>
                <li>Opt out of non-essential communications</li>
                <li>Withdraw consent for optional data collection</li>
                <li>Request information about how your data is used</li>
              </ul>
            </section>

            <section>
              <h2 className="font-heading font-bold text-lg mb-3">6. Data Retention</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                We retain your data for as long as your account is active or as needed to provide you 
                services. If you delete your account, we will delete or anonymize your personal data 
                within 30 days, except where retention is required by law.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-bold text-lg mb-3">7. Children&apos;s Privacy</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                HealthLink360 is not intended for children under 13. Users between 13-18 should have 
                parental consent. We do not knowingly collect data from children under 13.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-bold text-lg mb-3">8. Cookies and Tracking</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                We use essential cookies for authentication and session management. We do not use 
                tracking cookies for advertising purposes. You can control cookie settings in your 
                browser preferences.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-bold text-lg mb-3">9. Changes to This Policy</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                We may update this policy periodically. We will notify you of significant changes via 
                email or in-app notification. The "Last updated" date at the bottom indicates when 
                this policy was last revised.
              </p>
            </section>

            <section>
              <h2 className="font-heading font-bold text-lg mb-3">10. Contact Us</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                For privacy-related questions or to exercise your data rights, contact us at{' '}
                <a href="mailto:hello@healthlink360.ai" className="text-primary hover:underline">
                  hello@healthlink360.ai
                </a>
              </p>
            </section>

            <p className="text-xs text-muted-foreground pt-4 border-t border-border">
              Last updated: December 2025
            </p>
          </Card>
        </motion.div>
      </main>
    </div>
  );
}
